import React from 'react';
import { GetServerSidePropsResult } from 'next';
import PageContainer from 'components/global/PageContainer';
import FullPageFormWrapper from 'components/form-wizards/fullPageFormWrapper/FullPageFormWrapper';
import getLandingPageData from 'app-requests/content-type-loaders/landing-page-props';
import handleServerRedirect, {
  handleMaintenanceRedirect,
} from 'utils/handleServerRedirect';
import { APIError } from 'utils/errors/APIError';
import { APITransformError } from 'utils/errors/APITransformError';
import { LogError } from 'utils/logging/LogError';
import isDevelopment from 'utils/isDevelopment';
import {
  clickPortalRedirect,
  defaultRedirect,
  notFoundRedirect,
  SITE_TYPE,
} from 'consts';
import { SiteTypeError } from 'utils/errors/SiteTypeError';
import {
  ErrorPriority,
  ServerSidePageProps,
  TriadGetServerSidePropsContext,
} from 'types';

function LandingPageForm(props: {
  pageProps: ServerSidePageProps;
}): React.JSX.Element {
  const { pageProps } = props;

  return (
    <PageContainer pageProps={pageProps}>
      <FullPageFormWrapper />
    </PageContainer>
  );
}

export async function getServerSideProps(
  context: TriadGetServerSidePropsContext
): Promise<GetServerSidePropsResult<ServerSidePageProps | object>> {
  try {
    const pageProps = await getLandingPageData(context, false, true);
    pageProps.title = 'Full Page Form';
    return { props: pageProps };
  } catch (error: unknown) {
    if (
      error instanceof APIError &&
      error.statusCode === 404 &&
      context.req.url !== defaultRedirect
    ) {
      handleServerRedirect(defaultRedirect, context);
      return { props: {} };
    }

    if (error instanceof APIError && error.statusCode === 404) {
      handleServerRedirect(notFoundRedirect, context);
      return { props: {} };
    }

    if (
      error instanceof SiteTypeError &&
      error.siteType === SITE_TYPE.CLICK_PORTAL
    ) {
      handleServerRedirect(clickPortalRedirect, context);
      return { props: {} };
    }

    if (error instanceof APIError) {
      LogError(error, {
        description: `statusCode: ${error.statusCode}, route: landing/form`,
        endpoint: error.endpoint,
        priority: ErrorPriority.ONE,
      });
    } else if (error instanceof APITransformError) {
      LogError(error, {
        description: `method: ${error.method}, route: landing/form`,
        priority: ErrorPriority.ONE,
      });
    } else {
      LogError(error as Error, {
        description: `route: landing/form`,
        priority: ErrorPriority.ONE,
      });
    }

    if (isDevelopment()) {
      throw error;
    } else {
      handleMaintenanceRedirect(context, error as Error);
    }
    return { props: {} };
  }
}

export default LandingPageForm;
